import React, {useEffect, useMemo, useRef} from 'react';
import {SectionWrapper} from './TimeApprovalPage_styled';
import {APPROVAL_STATUS, BUTTON_COLOR, BUTTON_STYLE} from '../../../constants';
import TimeApprovalTable from './TimeApprovalTable';
import {Button, Tab, Tabs} from '@/web-components';
import {
	getPersonData,
	getProjectData,
	GROUP_BY,
	groupTimeRegsByEffectiveStatus,
	hasNewApprovalStatuses,
	ownedProjectPersonsQuery,
} from './TimeApprovalLogic';
import {synthesizeOwnedPersonsByStatus} from '../../../forecast-app/shared/util/TimesheetApprovalStatus';
import EmptyState from '../../../forecast-app/shared/components/empty-states/empty_state';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import Util from '../../../forecast-app/shared/util/util';
import SendTimeRegEmailMutation from '../../../mutations/send_time_reg_email_mutation';
import SendDeclinedTimeRegEmailReminderMutation from '../../../mutations/send_declined_time_reg_email_reminder_mutation';
import {useForecastFetchQuery} from '../../../forecast-app/shared/hooks/useForecastFetchQuery';
import {createToast} from '../../../forecast-app/shared/components/toasts/toast';
import CustomScrollDiv from '../../../forecast-app/shared/components/scroll-bars/custom_scroll_div';
import {hasPermission} from '../../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../Permissions';
import {createRefetchContainer, graphql} from 'react-relay';
import {withRouter} from 'react-router-dom';
import {injectIntl} from 'react-intl';
import {withSocketHandling} from '../../../socket/withSocketHandling';
import {groupBy} from 'lodash';
import {usingTimeApproval, usingTimeOffApproval} from '../../../forecast-app/shared/util/FeatureUtil';
import * as tracking from '../../../tracking';
import {getSocketConfig} from './TimeApprovalSocketHandling';
import {hideLoader, showLoader} from '../../../containers/global_loader';
import SageUtil from '../../../forecast-app/shared/util/sage/SageUtil';
import {getNewStatusEmptyState} from './TimeApprovalEmptyState';

const applyFilter = (timeReg, filterFunctions, filterOptions) => {
	if (!filterFunctions.timeRegFilter) return true;
	const timeRegPerson = timeReg.person;
	const timeRegProject = timeReg.project || timeReg.task?.project || timeReg.idleTime;
	// Flatten time regs to have them play well with filter
	const flattenedTimeReg = {
		...timeReg,
		project: timeRegProject,
		budgetType: timeRegProject.budgetType,
		person: timeRegPerson,
		role: timeRegPerson.role,
		department: timeRegPerson.department,
	};
	return filterFunctions.timeRegFilter(flattenedTimeReg, filterOptions);
};

const TimeApprovalSection = ({
	viewer,
	relay,
	setSocketVariables,
	setSocketConfig,
	filterFunctions,
	enabledColumns,
	currentViewingDate,
	intl,
	group,
	actualPersonId,
	personFilter,
	filterOptions,
	topLevelApprovalDisabled,
	companyLockedDate,
}) => {
	const {fetch, data} = useForecastFetchQuery(ownedProjectPersonsQuery);
	const isNewStatuses = hasNewApprovalStatuses();
	const useTimeApproval = usingTimeApproval(viewer.company.useTimeApproval);
	const useTimeOffApproval = usingTimeOffApproval(viewer.company.useTimeOffApproval);
	const showInvoicedTab = isNewStatuses && (useTimeApproval || !useTimeOffApproval);

	let timeRegs = viewer.company.timeRegistrations.edges.map(tReg => tReg.node);
	if (filterFunctions) {
		timeRegs = timeRegs.filter(tReg => applyFilter(tReg, filterFunctions, filterOptions));
	}
	const groupedTimeRegs = isNewStatuses ? groupTimeRegsByEffectiveStatus(timeRegs) : groupBy(timeRegs, 'approvalStatus');
	const showProjectActions = !SageUtil.syncTimeRegsFromForecast(viewer.company);

	/* On socket events we do not show the loader, but we do need to make sure we hide it
	 * because a socket triggered refetch can cancel and replace a variable triggered refetch
	 * causing the loader to never be hidden.
	 * */
	const refetchOnSocketEvent = variables => {
		relay.refetch(variables, null, () => hideLoader());
	};

	useEffect(() => {
		document.title = 'Time Approval - Forecast';
		tracking.trackPage('Time-approval');
		setSocketConfig(getSocketConfig(), refetchOnSocketEvent);
	}, []);

	const didMount = useRef(false);

	/* When the fetch variables change show the loader, refetch and hide the loader on a completed fetch
	 * */
	const refetchOnVariableChange = variables => {
		showLoader();
		relay.refetch(variables, null, () => hideLoader());
		setSocketVariables(variables);
	};

	useEffect(() => {
		// Only run if not mount
		if (!didMount.current) {
			didMount.current = true;
		} else {
			//we need to fetch now the time entries of each person
			const startDate = currentViewingDate.clone().startOf('week').format('YYYY-MM-DD');

			const endDate = currentViewingDate.clone().endOf('week').format('YYYY-MM-DD');

			refetchOnVariableChange({
				startDateString: startDate,
				endDateString: endDate,
				approvableTime: useTimeApproval,
				approvableTimeOff: useTimeOffApproval,
			});
		}
	}, [currentViewingDate, useTimeApproval, useTimeOffApproval]);

	useEffect(() => {
		fetch({personId: actualPersonId});
	}, []);

	const dateRange =
		currentViewingDate.clone().startOf('w').format('DD MMMM') +
		' - ' +
		currentViewingDate.clone().endOf('w').format('DD MMMM YYYY');

	const sendReminderEmail = (mutation, personIds, onSuccess) => {
		Util.CommitMutation(mutation, {receiverIds: personIds, dateRange}, onSuccess);
	};

	const showRemindAllModal = (personIds, bodyContent, mutation) => {
		const onSuccess = () => {
			createToast({duration: 5000, message: intl.formatMessage({id: 'approval.reminder_sent_to_all'})});
		};
		showModal({
			type: MODAL_TYPE.GENERIC,
			headerText: intl.formatMessage({id: 'timesheet.reminder_all_modal_header'}),
			content: <div className={'body-container'}>{bodyContent}</div>,
			className: 'default-warning-modal',
			buttons: [
				{
					text: intl.formatMessage({id: 'common.cancel'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
				},
				{
					text: intl.formatMessage({id: 'common.send'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.GREEN,
					callback: () => sendReminderEmail(mutation, personIds, onSuccess),
				},
			],
		});
	};

	const showRemindOneModal = (person, bodyMessageId, mutation) => {
		const displayName = [person.firstName, person.lastName].filter(Boolean).join(' ');
		const onSuccess = () => {
			createToast({
				duration: 5000,
				message: intl.formatMessage(
					{id: 'approval.reminder_sent_to_person'},
					{person: person.firstName || person.lastName}
				),
			});
		};
		showModal({
			type: MODAL_TYPE.GENERIC,
			headerText: intl.formatMessage({id: 'timesheet.reminder_modal_header'}),
			content: <div className={'body-container'}>{intl.formatMessage({id: bodyMessageId}, {person: displayName})}</div>,
			className: 'default-warning-modal',
			buttons: [
				{
					text: intl.formatMessage({id: 'common.cancel'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
				},
				{
					text: intl.formatMessage({id: 'common.send'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.GREEN,
					callback: () => sendReminderEmail(mutation, [person.id], onSuccess),
				},
			],
		});
	};

	const handleRemindAll = personIds =>
		showRemindAllModal(personIds, intl.formatMessage({id: 'timesheet.reminder_all_modal_body'}), SendTimeRegEmailMutation);
	const handleRemindAllDeclined = personIds =>
		showRemindAllModal(
			personIds,
			intl.formatMessage({id: 'timesheet.declined_reminder_all_modal_body'}, {count: personIds.length}),
			SendDeclinedTimeRegEmailReminderMutation
		);

	const handleRemindOne = person =>
		showRemindOneModal(person, 'timesheet.approval_reminder_modal_body', SendTimeRegEmailMutation);
	const handleRemindOneDeclined = person =>
		showRemindOneModal(person, 'timesheet.declined_reminder_modal_body', SendDeclinedTimeRegEmailReminderMutation);

	/**
	 * We need to calculate the first groupings for all tab first to get the numbers and the empty states correctly
	 */
	const firstGroupData = useMemo(() => {
		let res = {};
		if (group === GROUP_BY.PROJECT) {
			res[APPROVAL_STATUS.NOT_SUBMITTED] = getProjectData(groupedTimeRegs[APPROVAL_STATUS.NOT_SUBMITTED], null, true);
			res[APPROVAL_STATUS.SUBMITTED] = getProjectData(groupedTimeRegs[APPROVAL_STATUS.SUBMITTED], null, true);
			res[APPROVAL_STATUS.APPROVED] = getProjectData(groupedTimeRegs[APPROVAL_STATUS.APPROVED], null, true);
			if (isNewStatuses) {
				res[APPROVAL_STATUS.PENDING] = getProjectData(groupedTimeRegs[APPROVAL_STATUS.PENDING], null, true);
				res[APPROVAL_STATUS.DECLINED] = getProjectData(groupedTimeRegs[APPROVAL_STATUS.DECLINED], null, true);
				res[APPROVAL_STATUS.INVOICED] = getProjectData(groupedTimeRegs[APPROVAL_STATUS.INVOICED], null, true);
			}
		} else {
			// Group owned-persons by their week-level computed status. The helper consults
			// each person's time-regs (if any) and submit lock to decide which bucket they
			// belong in. Critical case: an owned person with no time-regs whose submit lock
			// advanced past the week is bucketed as SUBMITTED — they must show on the
			// Submitted tab (mirroring My Timesheets' "Submitted" badge + "Unsubmit Timesheet"
			// button for the same week), NOT on Not Submitted / Pending.
			const ownedPersonsByStatus = synthesizeOwnedPersonsByStatus(
				data?.viewer.company.person.ownedProjectPersons.edges,
				timeRegs,
				viewer.company,
				currentViewingDate,
				isNewStatuses
			);
			const submittedOwnedPersons = ownedPersonsByStatus.get(APPROVAL_STATUS.SUBMITTED) ?? [];
			const notSubmittedOwnedPersons = ownedPersonsByStatus.get(APPROVAL_STATUS.NOT_SUBMITTED) ?? [];
			const pendingOwnedPersons = ownedPersonsByStatus.get(APPROVAL_STATUS.PENDING) ?? [];

			// SUBMITTED bucket: synthesize 0h-submitted owned persons here so the Approval page
			// mirrors My Timesheets — if the owner sees "Submitted" badge + "Unsubmit Timesheet"
			// button, the approver should see them in the Submitted tab too. Without this, an
			// admin-submitted 0h timesheet is invisible on the Approval page entirely.
			res[APPROVAL_STATUS.SUBMITTED] = getPersonData(
				groupedTimeRegs[APPROVAL_STATUS.SUBMITTED],
				null,
				submittedOwnedPersons,
				res,
				personFilter,
				filterOptions
			);
			res[APPROVAL_STATUS.APPROVED] = getPersonData(groupedTimeRegs[APPROVAL_STATUS.APPROVED]);
			res[APPROVAL_STATUS.NOT_SUBMITTED] = getPersonData(
				groupedTimeRegs[APPROVAL_STATUS.NOT_SUBMITTED],
				null,
				isNewStatuses ? undefined : notSubmittedOwnedPersons,
				isNewStatuses ? undefined : res,
				isNewStatuses ? undefined : personFilter,
				isNewStatuses ? undefined : filterOptions
			);
			if (isNewStatuses) {
				res[APPROVAL_STATUS.DECLINED] = getPersonData(groupedTimeRegs[APPROVAL_STATUS.DECLINED]);
				res[APPROVAL_STATUS.INVOICED] = getPersonData(groupedTimeRegs[APPROVAL_STATUS.INVOICED]);
				res[APPROVAL_STATUS.PENDING] = getPersonData(
					groupedTimeRegs[APPROVAL_STATUS.PENDING],
					null,
					pendingOwnedPersons,
					res,
					personFilter,
					filterOptions
				);
			}
		}
		return res;
		// Narrow viewer.company deps to the specific fields read by the shared helper's
		// getLockedDate call (avoid memo invalidation on every time-reg refetch, since
		// viewer.company contains the timeRegistrations connection).
	}, [
		data,
		currentViewingDate,
		group,
		groupedTimeRegs,
		isNewStatuses,
		viewer.company.lockedPeriodYear,
		viewer.company.lockedPeriodMonth,
		viewer.company.lockedPeriodDay,
		viewer.company.modules,
	]);

	const uniquePersonIdsWithUnsubmittedTimeRegs =
		group === GROUP_BY.PERSON
			? firstGroupData[APPROVAL_STATUS.NOT_SUBMITTED].map(p => p.person.id)
			: getPersonData(groupedTimeRegs[APPROVAL_STATUS.NOT_SUBMITTED]).map(p => p.person.id);

	const uniquePersonIdsWithPendingTimeRegs = isNewStatuses
		? group === GROUP_BY.PERSON
			? firstGroupData[APPROVAL_STATUS.PENDING].map(p => p.person.id)
			: getPersonData(groupedTimeRegs[APPROVAL_STATUS.PENDING]).map(p => p.person.id)
		: [];

	const uniquePersonIdsWithDeclinedTimeRegs = isNewStatuses
		? group === GROUP_BY.PERSON
			? (firstGroupData[APPROVAL_STATUS.DECLINED] || []).map(p => p.person.id)
			: getPersonData(groupedTimeRegs[APPROVAL_STATUS.DECLINED] || []).map(p => p.person.id)
		: [];

	const submittedCount = firstGroupData[APPROVAL_STATUS.SUBMITTED]?.length ?? 0;
	const approvedCount = firstGroupData[APPROVAL_STATUS.APPROVED]?.length ?? 0;
	const notSubmittedCount = firstGroupData[APPROVAL_STATUS.NOT_SUBMITTED]?.length ?? 0;
	const pendingCount = firstGroupData[APPROVAL_STATUS.PENDING]?.length ?? 0;
	const declinedCount = firstGroupData[APPROVAL_STATUS.DECLINED]?.length ?? 0;
	const invoicedCount = firstGroupData[APPROVAL_STATUS.INVOICED]?.length ?? 0;

	const hasManageAllPermission = hasPermission(PERMISSION_TYPE.MANAGE_ACCOUNT_SETTINGS);

	const children = selectedTab => {
		// Empty-state branch
		if (!firstGroupData[selectedTab]?.length) {
			// Handle new approval statuses before the legacy chain
			if (
				isNewStatuses &&
				[APPROVAL_STATUS.PENDING, APPROVAL_STATUS.DECLINED, APPROVAL_STATUS.INVOICED].includes(selectedTab)
			) {
				const pageName = getNewStatusEmptyState(
					selectedTab,
					{
						submitted: submittedCount,
						approved: approvedCount,
						notSubmitted: notSubmittedCount,
						pending: pendingCount,
						declined: declinedCount,
						invoiced: invoicedCount,
					},
					hasManageAllPermission
				);
				if (pageName !== null) {
					return <EmptyState pageName={pageName} />;
				}
				return null;
			}

			// Legacy empty-state chain — SUBMITTED / NOT_SUBMITTED / APPROVED
			if (submittedCount + approvedCount + notSubmittedCount === 0) {
				return (
					<EmptyState
						pageName={
							hasManageAllPermission
								? EmptyState.EMPTY_STATE.APPROVAL_NO_REGISTERED
								: EmptyState.EMPTY_STATE.APPROVAL_NO_REGISTERED_ON_OWNED
						}
					/>
				);
			} else if (submittedCount + approvedCount === 0) {
				return (
					<EmptyState
						pageName={
							hasManageAllPermission
								? EmptyState.EMPTY_STATE.APPROVAL_NO_SUBMIT
								: EmptyState.EMPTY_STATE.APPROVAL_NO_SUBMIT_ON_OWNED
						}
					/>
				);
			} else if (submittedCount === 0 && approvedCount > 0 && notSubmittedCount > 0) {
				return <EmptyState pageName={EmptyState.EMPTY_STATE.APPROVAL_MISSING_SUBMITION} />;
			} else if (approvedCount === 0) {
				return <EmptyState pageName={EmptyState.EMPTY_STATE.APPROVAL_NOTHING_APPROVED} />;
			} else if (submittedCount + notSubmittedCount === 0) {
				return <EmptyState pageName={EmptyState.EMPTY_STATE.APPROVAL_ALL_APPROVED} />;
			} else if (submittedCount > 0 && notSubmittedCount === 0) {
				return <EmptyState pageName={EmptyState.EMPTY_STATE.APPROVAL_ALL_SUBMITTED} />;
			}
			return null;
		}

		// Non-empty render — unified for all tabs
		return (
			<>
				<div
					style={{
						position: 'sticky',
						left: '0',
						padding: '16px 16px',
						width: '100%',
						display: 'flex',
						justifyContent: 'flex-end',
					}}
				>
					{selectedTab === APPROVAL_STATUS.NOT_SUBMITTED && (
						<Button
							variant={Button.VARIANT.VERY_LIGHT_GRAY_OUTLINE}
							onClick={() => handleRemindAll(uniquePersonIdsWithUnsubmittedTimeRegs)}
						>
							{intl.formatMessage({id: 'timesheet.reminder_all_modal_header'})}
						</Button>
					)}
					{isNewStatuses && selectedTab === APPROVAL_STATUS.PENDING && (
						<Button
							variant={Button.VARIANT.VERY_LIGHT_GRAY_OUTLINE}
							onClick={() => handleRemindAll(uniquePersonIdsWithPendingTimeRegs)}
						>
							{intl.formatMessage({id: 'timesheet.reminder_all_modal_header'})}
						</Button>
					)}
					{isNewStatuses && selectedTab === APPROVAL_STATUS.DECLINED && (
						<Button
							variant={Button.VARIANT.VERY_LIGHT_GRAY_OUTLINE}
							onClick={() => handleRemindAllDeclined(uniquePersonIdsWithDeclinedTimeRegs)}
						>
							{intl.formatMessage({id: 'timesheet.reminder_all_modal_header'})}
						</Button>
					)}
				</div>
				<TimeApprovalTable
					onRemind={selectedTab === APPROVAL_STATUS.DECLINED ? handleRemindOneDeclined : handleRemindOne}
					sectionType={selectedTab}
					currentViewingDate={currentViewingDate}
					intl={intl}
					enabledColumns={enabledColumns}
					isGroupedByProject={group === GROUP_BY.PROJECT}
					firstGroupData={firstGroupData[selectedTab]}
					topLevelApprovalDisabled={topLevelApprovalDisabled}
					actualPersonId={actualPersonId}
					companyLockedDate={companyLockedDate}
					showProjectActions={showProjectActions}
				/>
			</>
		);
	};

	return (
		<SectionWrapper>
			<Tabs>
				{isNewStatuses && (
					<Tab
						cy={'time-approval-tab-pending'}
						title={intl.formatMessage({id: 'time_approval.pending'}) + ' (' + pendingCount + ')'}
					>
						<CustomScrollDiv>{children(APPROVAL_STATUS.PENDING)}</CustomScrollDiv>
					</Tab>
				)}

				<Tab
					cy={'time-approval-tab-submitted'}
					defaultSelected
					title={intl.formatMessage({id: 'time_approval.submitted'}) + ' (' + submittedCount + ')'}
				>
					<CustomScrollDiv>{children(APPROVAL_STATUS.SUBMITTED)}</CustomScrollDiv>
				</Tab>

				{(!isNewStatuses || notSubmittedCount > 0) && (
					<Tab
						cy={'time-approval-tab-not-submitted'}
						title={intl.formatMessage({id: 'time_approval.not_submitted'}) + ' (' + notSubmittedCount + ')'}
					>
						<CustomScrollDiv>{children(APPROVAL_STATUS.NOT_SUBMITTED)}</CustomScrollDiv>
					</Tab>
				)}

				{isNewStatuses && (
					<Tab
						cy={'time-approval-tab-declined'}
						title={intl.formatMessage({id: 'time_approval.declined'}) + ' (' + declinedCount + ')'}
					>
						<CustomScrollDiv>{children(APPROVAL_STATUS.DECLINED)}</CustomScrollDiv>
					</Tab>
				)}

				<Tab
					cy={'time-approval-tab-approved'}
					title={intl.formatMessage({id: 'common.approved'}) + ' (' + approvedCount + ')'}
				>
					<CustomScrollDiv>{children(APPROVAL_STATUS.APPROVED)}</CustomScrollDiv>
				</Tab>

				{showInvoicedTab && (
					<Tab
						cy={'time-approval-tab-invoiced'}
						title={intl.formatMessage({id: 'time_approval.invoiced'}) + ' (' + invoicedCount + ')'}
					>
						<CustomScrollDiv>{children(APPROVAL_STATUS.INVOICED)}</CustomScrollDiv>
					</Tab>
				)}
			</Tabs>
		</SectionWrapper>
	);
};

const TimeApprovalSectionQuery = graphql`
	query TimeApprovalSection_Query(
		$searchQuery: TaskSearchQueryType
		$startDateString: String
		$endDateString: String
		$approvableTime: Boolean
		$approvableTimeOff: Boolean
	) {
		viewer {
			actualPersonId
			projectOwner
			component(name: "time-approval-section")
			...TimeApprovalSection_viewer
				@arguments(
					searchQuery: $searchQuery
					startDateString: $startDateString
					endDateString: $endDateString
					approvableTime: $approvableTime
					approvableTimeOff: $approvableTimeOff
				)
		}
	}
`;

export {TimeApprovalSectionQuery};

export default withRouter(
	injectIntl(
		withSocketHandling(
			createRefetchContainer(
				TimeApprovalSection,
				{
					viewer: graphql`
						fragment TimeApprovalSection_viewer on Viewer
						@argumentDefinitions(
							searchQuery: {type: "TaskSearchQueryType"}
							startDateString: {type: "String"}
							endDateString: {type: "String"}
							approvableTime: {type: "Boolean"}
							approvableTimeOff: {type: "Boolean"}
						) {
							component(name: "time-approval-section")
							id
							company {
								id
								useTimeApproval
								useTimeOffApproval
								useInternalTimeApproval
								lockedPeriodYear
								lockedPeriodMonth
								lockedPeriodDay
								sageIntacctConnectionEnabled
								sageIntacctTimeRegsFromForecast
								modules {
									moduleType
								}
								timeRegistrations(
									first: 10000000
									startDate: $startDateString
									endDate: $endDateString
									searchQuery: $searchQuery
									approvableTime: $approvableTime
									approvableTimeOff: $approvableTimeOff
									validateAccess: true
								) @connection(key: "Company_timeRegistrations", filters: []) {
									edges {
										node {
											id
											approvalStatus
											invoiced
											noApprovalStatusChange
											day
											month
											year
											minutesRegistered
											billableMinutesRegistered
											billable
											notes
											role {
												id
												name
											}
											idleTime {
												id
												name
												isInternalTime
											}
											person {
												id
												firstName
												lastName
												fullName
												email
												profilePictureId
												startDate
												endDate
												monday
												tuesday
												wednesday
												thursday
												friday
												saturday
												sunday
												department {
													id
												}
												role {
													id
												}
											}
											project {
												id
												name
												projectColor
												companyProjectId
												customProjectId
												fullAccessToProject
												budgetType
												billable
												client {
													id
													name
												}
												projectPersons(first: 100, contactsOnly: true) {
													edges {
														node {
															person {
																id
																fullName
																profilePictureId
															}
														}
													}
												}
											}
											task {
												id
												name
												companyTaskId
												estimateForecast
												billable
												estimateForecastMinutes
												totalMinutesRegistered
												project {
													id
													name
													companyProjectId
													customProjectId
													projectColor
													fullAccessToProject
													estimationUnit
													minutesPerEstimationPoint
													budgetType
													billable
													client {
														id
														name
													}
													projectPersons(first: 100, contactsOnly: true) {
														edges {
															node {
																person {
																	id
																	fullName
																	profilePictureId
																}
															}
														}
													}
												}
											}
										}
									}
								}
							}
						}
					`,
				},
				graphql`
					query TimeApprovalSectionRefetchQuery(
						$searchQuery: TaskSearchQueryType
						$startDateString: String
						$endDateString: String
						$approvableTime: Boolean
						$approvableTimeOff: Boolean
					) {
						viewer {
							component(name: "time-approval-section")
							...TimeApprovalSection_viewer
								@arguments(
									searchQuery: $searchQuery
									startDateString: $startDateString
									endDateString: $endDateString
									approvableTime: $approvableTime
									approvableTimeOff: $approvableTimeOff
								)
						}
					}
				`
			)
		)
	)
);
